import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import glyphUrl from '../../ui/brand/gradex-glyph-lavender.svg';
import markUrl from '../../ui/brand/gradex-mark-violet.svg';
import wordmarkUrl from '../../ui/brand/gradex-wordmark-lavender.svg';
import { useReducedMotion } from '../../ui/motion.js';
import './auth.css';

const ROLES = ['teachers', 'leaders', 'educators'] as const;
const PURPOSES = ['matters', 'schools', 'connections', 'step', 'future'] as const;

/** How long the first pairing stays up (short, so the page feels alive at once). */
const FIRST_MS = 3500;
/** How long each later pairing stays up. */
const ROTATE_MS = 5200;
/** The third line follows the second by this long. */
const FOLLOW_MS = 700;

interface Position {
  current: number;
  previous: number | undefined;
}

/** Where a line rests when motion is reduced: its first phrase. */
const AT_REST: Position = { current: 0, previous: undefined };

const advance = (count: number) => (position: Position) => ({
  current: (position.current + 1) % count,
  previous: position.current,
});

/**
 * The phrases of one line, stacked in one grid cell so the line is always as
 * wide and tall as its longest phrase (nothing shifts as it rotates). The
 * current phrase rises in and the last one lifts away; only the current one
 * is exposed to screen readers.
 */
function Rotator({ words, position }: { words: readonly string[]; position: Position }) {
  return (
    <span className="rotator">
      {words.map((word, index) => (
        <span
          key={word}
          className="rotator__word"
          aria-hidden={index === position.current ? undefined : true}
          data-state={
            index === position.current
              ? 'current'
              : index === position.previous
                ? 'leaving'
                : 'idle'
          }
        >
          {word}
        </span>
      ))}
    </span>
  );
}

/**
 * The brand panel beside the sign-in form: the logo (the violet mark, which
 * glows softly so it stands out on the dark panel, and the lavender wordmark), a three-line headline
 * ("Built by" is fixed; the role changes, then the purpose a moment later),
 * a supporting line and a faint Gradex watermark. With reduced motion (the
 * system's or the in-app setting) it rests on the first phrases.
 */
export function AuthHero() {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [role, setRole] = useState<Position>({ current: 0, previous: undefined });
  const [purpose, setPurpose] = useState<Position>({ current: 0, previous: undefined });

  useEffect(() => {
    if (reduced) return undefined;
    let follow: number | undefined;
    let interval: number | undefined;
    const change = () => {
      if (document.hidden) return;
      setRole(advance(ROLES.length));
      follow = window.setTimeout(() => {
        setPurpose(advance(PURPOSES.length));
      }, FOLLOW_MS);
    };
    const first = window.setTimeout(() => {
      change();
      interval = window.setInterval(change, ROTATE_MS);
    }, FIRST_MS);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(interval);
      window.clearTimeout(follow);
    };
  }, [reduced]);

  return (
    <aside className="auth-hero" aria-label={t('signIn.hero.label')}>
      <img className="auth-hero__watermark" src={glyphUrl} alt="" width={640} height={640} />
      <div className="auth-hero__logo" role="img" aria-label="Gradex">
        <img className="auth-hero__mark" src={markUrl} alt="" width={48} height={48} />
        <img className="auth-hero__wordmark" src={wordmarkUrl} alt="" width={148} height={48} />
      </div>
      <div className="auth-hero__body">
        <p className="auth-hero__headline">
          <span className="auth-hero__line">{t('signIn.hero.built')}</span>
          <span className="auth-hero__line auth-hero__line--role">
            <Rotator
              words={ROLES.map((key) => t(`signIn.hero.roles.${key}`))}
              position={reduced ? AT_REST : role}
            />
          </span>
          <span className="auth-hero__line auth-hero__line--purpose">
            <Rotator
              words={PURPOSES.map((key) => t(`signIn.hero.purposes.${key}`))}
              position={reduced ? AT_REST : purpose}
            />
          </span>
        </p>
        <p className="auth-hero__support">{t('signIn.hero.support')}</p>
      </div>
    </aside>
  );
}
